import { useState, useCallback, useMemo, useEffect } from "react";
import { WaveCanvas } from "./components/wave-canvas";
import { useAudioManager } from "./hooks/use-audio-manager";
import { TimeDisplay } from "./components/time-display";
import { usePlaybackController } from "./hooks/use-playback-controller";
import { AudioControlsProvider } from "./context/audio-controls-provider";
import { useAuthorizedAudio } from "./hooks/use-authorized-audio";
import ControlsBar from "./components/control-bar";

export interface AudioPlayerProps {
  src?: string;
  payload?: string;
  id: string;
  title?: string;
  height?: number;
  waveColor?: string;
  progressColor?: string;
  barWidth?: number;
}

const AudioPlayer: React.FC<AudioPlayerProps> = ({
  src,
  payload,
  id,
  title,
  height = 32,
  waveColor = "#9e9e9e",
  progressColor = "#615FFF",
  barWidth = 2,
}) => {
  const [volume, setVolume] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  const [dragProgress, setDragProgress] = useState<number | null>(null);

  // Get audio source from authorized hook
  const { audioSrc, isResolving } = useAuthorizedAudio({ src, payload });
 
  // Get audio management
  const {
    isAudioReady,
    duration,
    currentTime,
    isPlaying,
    playbackRate,
    togglePlay,
    seek: audioSeek,
    setPlaybackRate: audioSetPlaybackRate,
    setVolume: audioSetVolume,
    skip,
  } = useAudioManager(id, audioSrc, title);
  
  const peaks = useMemo(() => {
    const count = 120;
    const seedBase = id || "static-wave";
    const rand = (seed: number) => {
      const combined = `${seedBase}_${seed}`;
      let hash = 0;
      for (let i = 0; i < combined.length; i++) {
        const char = combined.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash |= 0;
      }
      const x = Math.sin(hash * 9999) * 10000;
      return x - Math.floor(x);
    };
    const result: number[] = [];
    for (let i = 0; i < count; i++) {
      const base = Math.pow(rand(i), 0.6);
      const jitter = rand(i + 17) * 0.15;
      result.push(Math.min(1, Math.max(0.05, base + jitter)));
    }
    return result;
  }, [id]);

  // Calculate progress
  const progress = duration > 0 ? currentTime / duration : 0;
  const displayProgress = isDragging && dragProgress !== null ? dragProgress : progress;
  const displayCurrentTime = duration > 0 && isDragging && dragProgress !== null
    ? dragProgress * duration
    : currentTime;

  // Update audio volume
  useEffect(() => {
    audioSetVolume(volume);
  }, [volume, audioSetVolume]);

  const { cyclePlaybackRate } = usePlaybackController(
    playbackRate,
    audioSetPlaybackRate,
  );

  const handleSeek = useCallback(
    (percent: number) => {
      audioSeek(percent);
    },
    [audioSeek],
  );

  const handlePreviewSeek = useCallback((percent: number) => {
    setDragProgress(percent);
  }, []);

  const handleVolumeChange = useCallback((newVolume: number) => {
    setVolume(newVolume);
  }, []);

  const handleSkipBackward = useCallback(() => {
    skip(-15);
  }, [skip]);

  const handleSkipForward = useCallback(() => {
    skip(15);
  }, [skip]);

  const handleTogglePlay = useCallback(async () => {
    await togglePlay(playbackRate);
  }, [togglePlay, playbackRate]);

  const controlsValue = useMemo(
    () => ({
      isPlaying,
      isAudioReady,
      isResolving,
      togglePlay: handleTogglePlay,
      skipForward: handleSkipForward,
      skipBackward: handleSkipBackward,
      playbackRate,
      cyclePlaybackRate,
      volume,
      handleVolumeChange,
    }),
    [
      isPlaying,
      isAudioReady,
      isResolving,
      handleTogglePlay,
      handleSkipForward,
      handleSkipBackward,
      playbackRate,
      cyclePlaybackRate,
      volume,
      handleVolumeChange,
    ],
  );

  return (
    <div className="w-full bg-black bg-linear-to-tl from-[#191a1b] to-[#2d3436] p-4 border border-default-900">
      <TimeDisplay currentTime={displayCurrentTime} duration={duration} />

      <div className="relative mb-6">
        <WaveCanvas
          peaks={peaks}
          height={height}
          waveColor={waveColor}
          progressColor={progressColor}
          barWidth={barWidth}
          progress={displayProgress}
          id={id}
          onSeek={handleSeek}
          onPreviewSeek={handlePreviewSeek}
          onDragStart={() => {
            setIsDragging(true);
            setDragProgress(progress);
          }}
          onDragEnd={() => {
            setIsDragging(false);
            setDragProgress(null);
          }}
          isDragging={isDragging}
        />
      </div>

      <AudioControlsProvider value={controlsValue}>
        <ControlsBar />
      </AudioControlsProvider>
    </div>
  );
};

export default AudioPlayer;
